import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, List, ListItem, ListItemText, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { SetupChargeService } from "../../../../shared/services/api/setupCharge/SetupChargeService";
import { ISetupCharge } from "../../../../shared/services/api/setupCharge/Types";
import { setupChargeDateToIso, setupChargeFormatCurrency, setupChargeToday, setupChargeToNumber } from "../../utils/setupChargeFormat";
import { setupChargeActionState } from "../TableSetupCharge";

export type TSetupChargeBatchAction = "cancel" | "resend" | "repass";

interface ModalSetupChargeBatchProps {
	/** `null` mantém o modal fechado. */
	action: TSetupChargeBatchAction | null;
	charges: ISetupCharge[];
	onClose: () => void;
	/** Chamado ao fechar depois de processar, para a lista recarregar. */
	onFinished: () => void;
}

interface IItemResult {
	charge: ISetupCharge;
	ok: boolean;
	message: string;
}

const LABELS: Record<TSetupChargeBatchAction, string> = {
	cancel: "Cancelar taxas",
	resend: "Reenviar links",
	repass: "Registrar repasses",
};

const SUCCESS_MESSAGES: Record<TSetupChargeBatchAction, string> = {
	cancel: "Cancelada.",
	resend: "Link reenviado.",
	repass: "Repasse registrado.",
};

/** Valor que o lote repassa para cada taxa: sempre o esperado, nunca digitado (valor diferente exige justificativa, então é individual). */
const expectedRepassOf = (charge: ISetupCharge): number | undefined => setupChargeToNumber(charge.expectedRepass);

/** Ação em lote: executa uma a uma, na mesma regra da ação individual, e mostra o resultado de cada taxa. */
export const ModalSetupChargeBatch = ({ action, charges, onClose, onFinished }: ModalSetupChargeBatchProps) => {
	const [reason, setReason] = useState("");
	const [reasonError, setReasonError] = useState<string | null>(null);
	const [repassDate, setRepassDate] = useState(setupChargeToday());
	const [dateError, setDateError] = useState<string | null>(null);
	const [note, setNote] = useState("");
	const [isRunning, setIsRunning] = useState(false);
	const [results, setResults] = useState<IItemResult[] | null>(null);

	useEffect(() => {
		if (!action) return;
		setReason("");
		setReasonError(null);
		setRepassDate(setupChargeToday());
		setDateError(null);
		setNote("");
		setResults(null);
		setIsRunning(false);
	}, [action]);

	if (!action) return null;

	const eligible = charges.filter((charge) => setupChargeActionState(charge, action).allowed && (action !== "repass" || (expectedRepassOf(charge) ?? 0) > 0));
	const skipped = charges.length - eligible.length;
	const label = LABELS[action];
	const repassTotal = action === "repass" ? eligible.reduce((sum, charge) => sum + (expectedRepassOf(charge) ?? 0), 0) : 0;

	const handleRun = async () => {
		if (action === "cancel" && !reason.trim()) {
			setReasonError("Informe o motivo do cancelamento.");
			return;
		}
		if (action === "repass") {
			if (!repassDate) {
				setDateError("Informe a data do repasse.");
				return;
			}
			if (repassDate > setupChargeToday()) {
				setDateError("A data do repasse não pode ser futura.");
				return;
			}
		}
		setReasonError(null);
		setDateError(null);
		setIsRunning(true);
		const done: IItemResult[] = [];
		for (const charge of eligible) {
			let response;
			if (action === "cancel") response = await SetupChargeService.cancel(charge.id, { reason: reason.trim() });
			else if (action === "resend") response = await SetupChargeService.resend(charge.id);
			else response = await SetupChargeService.repass(charge.id, { amount: expectedRepassOf(charge) as number, repassDate: setupChargeDateToIso(repassDate), note: note.trim() || undefined });

			if (response instanceof Error) done.push({ charge, ok: false, message: response.message });
			else done.push({ charge, ok: true, message: SUCCESS_MESSAGES[action] });
			setResults([...done]);
		}
		setIsRunning(false);
	};

	const handleClose = () => {
		if (isRunning) return;
		if (results) onFinished();
		onClose();
	};

	const failures = results?.filter((item) => !item.ok).length ?? 0;

	return (
		<Dialog open onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle>{label} selecionadas</DialogTitle>
			<DialogContent dividers>
				{!results && !isRunning && (
					<Box>
						<Typography mb={2}>
							{eligible.length} {eligible.length === 1 ? "taxa será processada" : "taxas serão processadas"}.
							{skipped > 0 ? ` ${skipped} ${skipped === 1 ? "foi ignorada" : "foram ignoradas"} por não permitir esta ação no estado atual.` : ""}
						</Typography>
						{action === "cancel" && (
							<TextField fullWidth required multiline minRows={2} label="Motivo do cancelamento" value={reason} onChange={(e) => setReason(e.target.value)} error={!!reasonError} helperText={reasonError} />
						)}
						{action === "repass" && (
							<Box>
								<Alert severity="info" sx={{ mb: 2 }}>
									Cada taxa é repassada pelo valor esperado (valor × % do snapshot). Para repassar outro valor, use o repasse individual, que exige justificativa.
								</Alert>
								<List dense disablePadding sx={{ mb: 2, maxHeight: 220, overflow: "auto" }}>
									{eligible.map((charge) => (
										<ListItem key={charge.id} disableGutters>
											<ListItemText primary={`${charge.payerName ?? ""} · ${charge.clientName ?? ""}`} secondary={`Cobrado ${setupChargeFormatCurrency(charge.amount)} · repasse esperado ${setupChargeFormatCurrency(expectedRepassOf(charge))}`} />
										</ListItem>
									))}
								</List>
								<Typography fontWeight="bold" mb={2}>
									Total a repassar: {setupChargeFormatCurrency(repassTotal)}
								</Typography>
								<TextField
									fullWidth
									required
									type="date"
									label="Data do repasse"
									InputLabelProps={{ shrink: true }}
									inputProps={{ max: setupChargeToday() }}
									value={repassDate}
									onChange={(e) => setRepassDate(e.target.value)}
									error={!!dateError}
									helperText={dateError}
									sx={{ mb: 2 }}
								/>
								<TextField fullWidth multiline minRows={2} label="Observação (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
							</Box>
						)}
					</Box>
				)}

				{(isRunning || results) && (
					<Box>
						{isRunning && <LinearProgress variant="determinate" value={((results?.length ?? 0) / Math.max(eligible.length, 1)) * 100} sx={{ mb: 2 }} />}
						{results && !isRunning && (
							<Alert severity={failures ? "warning" : "success"} sx={{ mb: 2 }}>
								{results.length - failures} de {results.length} concluídas{failures ? `, ${failures} com problema` : ""}.
							</Alert>
						)}
						<List dense disablePadding>
							{(results ?? []).map((item) => (
								<ListItem key={item.charge.id} disableGutters>
									<ListItemText
										primary={`${item.charge.payerName ?? ""} · ${item.charge.clientName ?? ""}`}
										secondary={item.message}
										secondaryTypographyProps={{ color: item.ok ? "textSecondary" : "error" }}
									/>
								</ListItem>
							))}
						</List>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isRunning}>
					{results ? "Fechar" : "Voltar"}
				</Button>
				{!results && (
					<Button variant="contained" disableElevation color={action === "cancel" ? "error" : "primary"} onClick={handleRun} disabled={isRunning || eligible.length === 0} startIcon={isRunning ? <CircularProgress size={16} /> : undefined}>
						{label}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
